import { matchesSearch } from '@webbuilder/shared-types'
import type { ReactNode } from 'react'
/**
 * La tabla de ventas, sin la pantalla que la rodea.
 *
 * Vive aparte de `OrdersPanel` desde que la ficha de una oferta también enseña
 * lo que ha vendido: son la misma tabla —las mismas columnas, el mismo buscador,
 * los mismos filtros y la misma ficha de venta al abrir una fila— y escribirla
 * dos veces habría dejado dos listas de cobros que se separan al primer retoque.
 * Es lo mismo que hizo `BookingsTable` con las citas.
 *
 * No pide los datos: los recibe. Quien la usa es quien sabe qué cobros le tocan
 * —todos, en Cobros; los de una oferta, en su ficha— y quien tiene que volver a
 * pedirlos cuando algo cambia.
 *
 * Sólo se lee. Un cobro lo escribe el webhook de Stripe cuando el pago se
 * confirma, así que aquí no hay nada que crear, editar ni borrar — y no lo
 * habrá: inventar o corregir una venta a mano sería contradecir a Stripe, que es
 * quien tiene el dinero.
 *
 * Lo que se enseña sale del cobro y no de la oferta: el título es la copia que
 * se guardó al comprar, para que una oferta renombrada o retirada no cambie el
 * historial de lo que alguien compró.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { usePaged, Pagination } from '../Pagination'
import { formatDashboardListDate } from '../../../lib/dashboardListDate'
import { useI18n } from '../../../lib/i18n'
import { useNewItems } from '../../../lib/sectionNewItems'
import { NewItemDot } from '../NewItemDot'
import { FULFILLMENT_KEY, awaitsShipping, fulfillmentTone, orderCountTitle, orderItemSummary, orderItemsCompact, orderProductCount, orderStatusLabel, orderStatusTone } from './offerShared'
import { OrderDetailDialog } from './OrderDetailDialog'
import { PAYMENTS_UPSELL, UpsellCard } from '../FeatureUpsell'
import { disputeNeedsAction, disputeUnderReview, markOrderSeen, type Order } from '../../../lib/api'
import { csvAmount, downloadCsv } from '../../../lib/csv'
import { minorUnitFactor } from './offerShared'
import {
  IconAdjustmentsHorizontal,
  IconAlertTriangle,
  IconCalendarEvent,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconDots,
  IconDownload,
  IconEye,
  IconLayoutGrid,
  IconMail,
  IconSearch,
  IconShoppingCart,
  IconTag,
  IconTruckDelivery,
  IconUser,
  IconX,
} from '@tabler/icons-react'
import { moneyLocale } from '../../../lib/region'
import { useSearchPlaceholder } from '../../../hooks/useSearchPlaceholder'
import { openOnRowClick } from '../rowOpen'

const PAGE_SIZE = 25

/**
 * Si el cobro está en disputa, sin importar en qué punto.
 *
 * Las dos mitades ya existen por separado —una pide respuesta, la otra espera
 * veredicto— y se distinguen porque se ofrecen distinto. Para decidir si la
 * celda del importe habla de dinero o de la disputa da igual cuál sea: mientras
 * hay una, ese dinero no es seguro.
 */
const orderDisputed = (status: string | null | undefined): boolean =>
  disputeNeedsAction(status) || disputeUnderReview(status)

type OrderSort = 'recent' | 'oldest' | 'amountHigh' | 'amountLow'
/** Ventanas de tiempo en días. `all` es «desde siempre». */
type PeriodFilter = 'all' | '7' | '30' | '90'

/* Constante de módulo: guarda la clave y quien la use la pasa por su `t`. */
const PERIOD_KEY: Record<PeriodFilter, string> = {
  all: 'orders.period.all',
  '7': 'orders.period.7',
  '30': 'orders.period.30',
  '90': 'orders.period.90',
}

/**
 * Cómo llama Contactos a una persona que tiene correo.
 *
 * La ficha de alguien no se guía por un id de cobro sino por su dirección en
 * minúsculas, que es como el servidor agrupa a quien escribe y a quien compra.
 * Sin correo no hay a quién abrir: se devuelve vacío y la celda se queda quieta.
 */
function contactIdentity(email: string | null | undefined): string {
  const clean = (email ?? '').trim().toLowerCase()
  return clean ? `email:${clean}` : ''
}

/** Milisegundos de una fecha, o 0: ordenar con `NaN` deja la lista en desorden. */
function dateValue(value: string | null | undefined): number {
  if (!value) return 0
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : 0
}


/** Como lo formatea el servidor, pero para un total que aquí se acaba de sumar. */
function formatTotal(amount: number, currency: string): string {
  const upper = (currency || 'usd').toUpperCase()
  try {
    /* Con el locale de la REGIÓN del proyecto, no con uno fijo: escrito a mano
       decía «MX$» en todos los proyectos, fuera cual fuera su país. El importe
       es de un negocio concreto, así que se escribe como allí. Ver `region.tsx`. */
    return new Intl.NumberFormat(moneyLocale(), { style: 'currency', currency: upper }).format(amount / 100)
  } catch {
    // Una moneda que `Intl` no conoce no puede tirar la pantalla entera.
    return `${(amount / 100).toFixed(2)} ${upper}`
  }
}


export function OrdersTable({
  projectId, token, orders, failed, failureReason, refreshControl, onReload,
  record, onRecordChange, onOpenContact, onOpenOffer,
  disputedOnly = false, onConsumeDisputedOnly, canSell = true, onOpenPlans,
  scopedToOffer = false, loading = false, defaultToShip = false, empty,
}: {
  projectId: string
  token: string
  /** Los cobros que le tocan a esta pantalla, ya pedidos por quien llama. */
  orders: Order[]
  failed: boolean
  failureReason: string
  /** Volver a pedirlos: al fallar, y al cerrar una venta que pudo reembolsarse. */
  refreshControl?: ReactNode
  onReload: () => void
  /**
   * Si el plan da para cobrar.
   *
   * Sin ventas y sin plan que las permita, «todavía no hay ventas» describe algo
   * que no puede pasar: no es que no hayan comprado, es que no se puede cobrar
   * todavía. Ahí el hueco lo ocupa la oferta.
   */
  canSell?: boolean
  onOpenPlans?: (() => void) | undefined
  /** La venta a abrir al entrar. Viene de la campana, que enlaza a una concreta. */
  record?: string | null | undefined
  onRecordChange?: ((record: string | null) => void) | undefined
  /**
   * Entrar ya filtrado por disputadas. Lo pide el aviso de arriba cuando hay
   * varias: con una sola abre su ficha y no llega a pasar por aquí.
   */
  disputedOnly?: boolean
  /** Se consume al aplicarlo: es un empujón de una vez, no un modo pegado. */
  onConsumeDisputedOnly?: (() => void) | undefined
  /** Abre la ficha del comprador en Contactos, como hace Formularios. */
  onOpenContact?: ((identity: string) => void) | undefined
  /** Abre la oferta que se vendió, para editarla. */
  onOpenOffer?: ((offerId: string) => void) | undefined
  /**
   * Dentro de la ficha de UNA oferta.
   *
   * Todas las ventas son suyas, así que su título repetiría el mismo texto en
   * cada fila y el filtro por oferta tendría una sola opción. La columna que
   * manda pasa a ser el comprador, que es lo que distingue una venta de otra
   * cuando la oferta ya se sabe.
   */
  scopedToOffer?: boolean
  /**
   * Si los cobros todavía vienen de camino.
   *
   * Por defecto no: en Cobros la espera la hace la sección entera —cabecera
   * incluida— antes de llegar aquí. Dentro de una pestaña no hay sección que
   * esperar, y sin esto la tabla enseñaba «todavía no hay ventas» durante el
   * viaje, que es decir que no hay nada cuando aún no se sabe.
   */
  loading?: boolean
  /**
   * Entrar con «Por enviar» encendido si hay algo que mandar. Lo pide Envíos,
   * donde lo pendiente es lo que se viene a ver; si no hay nada, la lista
   * entera, que una vacía diría que no hay pedidos.
   */
  defaultToShip?: boolean
  /** El estado vacío de quien llama, cuando «todavía no hay ventas» no es la frase. */
  empty?: { title: string; body: string } | undefined
}) {
  const { t } = useI18n()
  const isNewOrder = useNewItems('orders', orders, !loading && !failed)
  const searchPlaceholder = useSearchPlaceholder('orders.searchPlaceholder')
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<OrderSort>('recent')
  const [offerFilter, setOfferFilter] = useState('all')
  const [period, setPeriod] = useState<PeriodFilter>('all')
  /* Sólo las que el vendedor todavía puede defender. Nace de lo que pidió el
     aviso, y a partir de ahí se apaga y se enciende como cualquier otro filtro:
     una vez dentro, esto es la lista de cobros y no una pantalla aparte. */
  const [disputed, setDisputed] = useState(disputedOnly)
  /* Los productos físicos que faltan por mandar: el trabajo pendiente de esta
     lista, con su propio interruptor como el de las disputas. */
  const [toShip, setToShip] = useState(() => defaultToShip && orders.some(awaitsShipping))
  const [filterOpen, setFilterOpen] = useState(false)
  const [openOrder, setOpenOrder] = useState<Order | null>(null)
  /* El menú de la fila sólo se ve en el teléfono —lo esconde el CSS en cuanto
     hay sitio para el botón de abrir—, pero se pinta siempre: decidir aquí si
     existe obligaría a medir el ancho en JavaScript para algo que la hoja de
     estilos ya sabe. */
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  /**
   * Dónde se pinta el menú de la fila; se mide al abrirlo, como en Ofertas.
   *
   * Y se pinta en `body`, no dentro de la fila: la tabla vive en una caja con
   * scroll horizontal —`dash-cms-table-scroll`—, y una caja que desplaza recorta
   * lo que sobresale. Dentro de la ficha de una oferta la columna es más corta
   * que en Cobros, así que el menú caía fuera de la caja y se veía cortado a la
   * mitad. Portado y con `position: fixed` no depende de dónde cuelgue.
   */
  const [listMenu, setListMenu] = useState<{ top: number; left: number; maxHeight: number; up: boolean }>({ top: 0, left: 0, maxHeight: 0, up: false })

  const searchRef = useRef<HTMLInputElement>(null)
  const filterRef = useRef<HTMLDivElement>(null)


  /**
   * El empujón del aviso se aplica y se consume.
   *
   * Si se quedara puesto, volver a Cobros desde el menú lateral una hora
   * después seguiría enseñando la lista filtrada sin que nadie lo hubiera
   * cobro — y con las disputas ya resueltas, vacía.
   */
  useEffect(() => {
    if (!disputedOnly) return
    setDisputed(true)
    onConsumeDisputedOnly?.()
  }, [disputedOnly, onConsumeDisputedOnly])

  /** El popover se cierra al pulsar fuera o con Escape, como el de Ofertas. */
  useEffect(() => {
    if (!filterOpen) return
    const onDown = (event: PointerEvent) => {
      if (!filterRef.current?.contains(event.target as Node)) setFilterOpen(false)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setFilterOpen(false) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [filterOpen])

  /**
   * La venta que enlaza la campana se abre sola al llegar.
   *
   * Se busca en las filas que hay y no se espera a nada: la tabla recibe los
   * cobros ya pedidos, así que mientras viajan esta lista está vacía y aquí no
   * se encuentra a nadie. La ficha se pinta con la fila, y abrir con un cobro a
   * medias enseñaría una cabecera vacía mientras llega el detalle.
   *
   * Si ese id ya no está —una venta borrada— no pasa nada y se queda la lista,
   * que es lo que se venía a ver.
   */
  useEffect(() => {
    if (!record) return
    const target = orders.find((order) => order.id === record)
    if (target) setOpenOrder(target)
  }, [record, orders])

  /**
   * Abrir una venta apaga su aviso.
   *
   * En el servidor y no aquí: la campana la mira el mismo dueño desde el
   * teléfono y desde el escritorio, y una marca guardada en este navegador
   * volvería a encender el aviso en el otro. Nadie espera a que termine — el
   * aviso ya cumplió su papel en cuanto la ficha se abrió.
   */
  useEffect(() => {
    if (!openOrder) return
    void markOrderSeen(projectId, openOrder.id, token).catch(() => {})
  }, [openOrder, projectId, token])

  /** Y el de la fila, igual que en Envíos y Contactos. */
  useEffect(() => {
    if (!menuOpenId) return
    const onDown = (event: PointerEvent) => {
      const el = event.target as Element | null
      /* El menú vive en `body`, así que pulsar DENTRO de él cae fuera de su
         botón: sin la segunda comprobación se cerraba antes de que el clic
         llegara a la opción que se acababa de pulsar. */
      if (!el?.closest?.('.dash-project-row-menu-wrap') && !el?.closest?.('.dash-order-row-menu-portal')) setMenuOpenId(null)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setMenuOpenId(null) }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('pointerdown', onDown); document.removeEventListener('keydown', onKey) }
  }, [menuOpenId])

  /* Las ofertas que han vendido algo, para el filtro. Una oferta borrada deja
     sus cobros con `offerId` nulo: se agrupan bajo su título copiado, que es
     lo único que queda de ella. */
  const soldOffers = useMemo(() => {
    const byId = new Map<string, string>()
    for (const order of orders) {
      if (order.offerId && !byId.has(order.offerId)) byId.set(order.offerId, order.offerTitle || t('common.untitled'))
    }
    return [...byId].map(([id, title]) => ({ id, title })).sort((a, b) => a.title.localeCompare(b.title, 'es'))
    /* `t` entre las dependencias porque «Sin título» se escribe aquí dentro: sin
       ella la lista se quedaba en el idioma que hubiera al cargar. */
  }, [orders, t])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const since = period === 'all' ? 0 : Date.now() - Number(period) * 24 * 60 * 60 * 1000

    const rows = orders.filter((order) => {
      if (offerFilter !== 'all' && order.offerId !== offerFilter) return false
      if (disputed && !disputeNeedsAction(order.disputeStatus)) return false
      if (toShip && !awaitsShipping(order)) return false
      if (since && dateValue(order.createdAt) < since) return false
      if (!needle) return true
      /* «1001» o «#1001»: el número del pedido, tal como lo dice el cliente. */
      if (order.number && String(order.number).startsWith(needle.replace(/^#/, ''))) return true
      return matchesSearch(order.offerTitle, needle)
        || matchesSearch(order.buyerEmail, needle)
        || matchesSearch(order.buyerName, needle)
    })

    return [...rows].sort((a, b) => {
      /* Con el filtro puesto manda el plazo y no la fecha de venta: lo que se
         viene a hacer aquí es atender la que se pierde antes. Las que no traen
         fecha van detrás, no delante. */
      if (disputed) {
        const dueOf = (order: Order) => {
          const value = order.disputeDueBy ? new Date(order.disputeDueBy).getTime() : NaN
          return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY
        }
        const gap = dueOf(a) - dueOf(b)
        if (gap !== 0) return gap
      }
      switch (sort) {
        case 'oldest': return dateValue(a.createdAt) - dateValue(b.createdAt)
        case 'amountHigh': return b.amountTotal - a.amountTotal
        case 'amountLow': return a.amountTotal - b.amountTotal
        default: return dateValue(b.createdAt) - dateValue(a.createdAt)
      }
    })
  }, [orders, query, sort, offerFilter, period, disputed, toShip])

  /**
   * Lo que se está viendo, a CSV.
   *
   * Los importes en columnas de números —importe, devuelto, impuesto,
   * descuento— y sin símbolo: una hoja de cálculo suma números, y quien exporta
   * ventas lo hace para sumarlas. La moneda va en su propia columna.
   */
  const exportCsv = () => {
    downloadCsv(
      t('orders.csv.filename'),
      [
        t('orders.csv.date'),
        t('orders.csv.offer'),
        t('orders.csv.variant'),
        t('orders.csv.quantity'),
        t('orders.csv.buyer'),
        t('orders.csv.email'),
        t('orders.csv.amount'),
        t('orders.csv.currency'),
        t('orders.csv.refunded'),
        t('orders.csv.status'),
        t('orders.csv.dispute'),
        /* El envío, vacío en lo que no es físico: con estas columnas se suben
           las direcciones a la web de la paquetería de una sola vez. */
        t('orders.csv.fulfillment'),
        t('orders.csv.shipName'),
        t('orders.csv.shipPhone'),
        t('orders.csv.shipLine1'),
        t('orders.csv.shipLine2'),
        t('orders.csv.shipCity'),
        t('orders.csv.shipState'),
        t('orders.csv.shipPostalCode'),
        t('orders.csv.shipCountry'),
        t('orders.csv.shipMethod'),
        t('orders.csv.carrier'),
        t('orders.csv.tracking'),
        t('orders.csv.id'),
      ],
      visible.map((order) => [
        order.createdAt,
        order.offerTitle,
        /* Del carrito, cada cosa con lo suyo en la misma celda. */
        order.items?.length ? orderItemSummary(order) : order.variantLabel ?? '',
        String(order.quantity ?? 1),
        order.buyerName ?? '',
        order.buyerEmail ?? '',
        csvAmount(order.amountTotal, minorUnitFactor(order.currency)),
        (order.currency || '').toUpperCase(),
        csvAmount(order.amountRefunded, minorUnitFactor(order.currency)),
        orderStatusLabel(order.status, t),
        order.disputeStatus ? t(disputeNeedsAction(order.disputeStatus) ? 'orders.disputed' : 'orders.underReview') : '',
        order.fulfillmentStatus ? t(FULFILLMENT_KEY[order.fulfillmentStatus]) : '',
        order.shippingName ?? '',
        order.shippingPhone ?? '',
        order.shippingAddress?.line1 ?? '',
        order.shippingAddress?.line2 ?? '',
        order.shippingAddress?.city ?? '',
        order.shippingAddress?.state ?? '',
        order.shippingAddress?.postalCode ?? '',
        order.shippingAddress?.country ?? '',
        order.shippingRateName ?? '',
        order.carrier ?? '',
        order.trackingNumber ?? '',
        order.id,
      ]),
    )
  }

  const paged = usePaged(visible, PAGE_SIZE, `${query}|${sort}|${offerFilter}|${period}|${disputed}|${toShip}`)
  /* El resumen cuenta lo filtrado y no todo: si acabas de acotar a una oferta y
     a treinta días, el total que quieres ver es el de eso. */
  const filtersOn = offerFilter !== 'all' || period !== 'all' || disputed || toShip
  /* Cuántas hay ahora mismo, sobre todos los cobros y no sobre lo ya filtrado:
     el interruptor tiene que decir cuántas existen, no cuántas quedan después
     de acotar por oferta y por fecha. */
  const disputedCount = useMemo(
    () => orders.filter((order) => disputeNeedsAction(order.disputeStatus)).length,
    [orders],
  )

  const toShipCount = useMemo(() => orders.filter(awaitsShipping).length, [orders])

  const resetFilters = () => { setOfferFilter('all'); setPeriod('all'); setDisputed(false); setToShip(false) }

  /* Sigue visible mientras se despide, aunque los datos ya hayan llegado. */
  const ordersLoader = useRecordLoader(loading)
  if (ordersLoader.visible) return <SectionLoader loader={ordersLoader} label={t('orders.loading')} />

  /* Sin ventas y sin plan que las permita, la pantalla es la oferta y nada más.
     Un buscador y un embudo sobre una lista que no puede existir todavía sólo
     invitan a comprobar que, en efecto, no encuentran nada. */
  const sellLocked = orders.length === 0 && !canSell && Boolean(onOpenPlans)

  /*
   * Y con cobros pero sin poder cobrar, la oferta va ARRIBA, en corto.
   *
   * «Sin cobrar» no quiere decir «sin cobros»: una oferta gratuita reclamada
   * deja su fila aquí, y en cuanto había una, `sellLocked` pasaba a falso y la
   * oferta desaparecía para siempre — justo de la pantalla que se abre a
   * preguntar cuánto se ha vendido, y justo en el plan donde la respuesta es
   * «nada, porque todavía no puedes». Con lista, la oferta no puede ocupar su
   * sitio: se pone encima, de un renglón, y lo de abajo se sigue leyendo.
   */
  const sellBanner = orders.length > 0 && !canSell && Boolean(onOpenPlans) && !scopedToOffer

  /* Y con la lista vacía tampoco: ni buscador, ni filtros. Es el mismo
     motivo del candado de arriba —«un buscador y un embudo sobre una lista
     que no puede existir todavía»— y vale igual cuando la lista puede
     existir pero aún no tiene nada. Los pedidos no se crean desde aquí, así
     que la barra no se lleva ninguna salida al irse. Vuelve con el primer
     pedido, o mientras quede algo escrito en la búsqueda. */
  /*
   * Dentro de una oferta también hay barra, igual que en la ficha de un servicio.
   *
   * Estuvo quitada con el razonamiento de que la lista de UNA oferta ya viene
   * acotada y un buscador encima de dos filas sólo puede quitar lo que se acaba
   * de venir a ver. Suena bien con dos filas y deja de sonar bien con sesenta:
   * una oferta que vende se mira para encontrar UNA compra —quién fue, cuándo—
   * y sin buscador eso es recorrer páginas a ojo.
   *
   * Y la de al lado ya lo hacía: la ficha de un servicio con agenda tiene su
   * buscador y su cabecera sobre la misma clase de lista. Dos fichas hermanas
   * con dos maneras distintas de mirar lo mismo era la diferencia más difícil
   * de justificar de las dos pantallas.
   *
   * El filtro por oferta sigue fuera cuando hay una sola —lo decide su propia
   * condición, más abajo—, que es lo único que de verdad no aplica aquí.
   */
  const showToolbar = !sellLocked && (orders.length > 0 || query.trim() !== '')


  return (
    <>
      {sellBanner && onOpenPlans ? (
        <UpsellCard slim standalone copy={PAYMENTS_UPSELL} onOpenPlans={onOpenPlans} />
      ) : null}
      {!showToolbar && refreshControl ? <div className="dash-toolbar-lead">{refreshControl}</div> : null}
      {showToolbar ? (
      <header className="dash-toolbar dash-layered-toolbar">
        <div className="dash-toolbar-lead">
            {refreshControl}
          <div className="dash-toolbar-search">
          <button type="button" className="dash-toolbar-search-icon" aria-label={t('orders.searchAria')} onClick={() => searchRef.current?.focus()}>
            <IconSearch size={16} />
          </button>
          <input
            ref={searchRef}
            type="search"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Escape') { setQuery(''); searchRef.current?.blur() } }}
          />
          {query ? (
            <button type="button" className="dash-toolbar-search-clear" aria-label={t('orders.clearSearch')} onClick={() => { setQuery(''); searchRef.current?.focus() }}>
              <IconX size={13} />
            </button>
          ) : null}
        </div>

        </div>

          <div className="dash-toolbar-controls">
          {/* Exportar lo que se ve, junto a los filtros: el filtro ES la
              selección, y el botón tiene que estar donde se acaba de filtrar. */}
          <button
            className="dash-filter-trigger"
            type="button"
            data-tooltip={t('orders.exportCsv')}
            aria-label={t('orders.exportCsv')}
            disabled={visible.length === 0}
            onClick={exportCsv}
          >
            <IconDownload size={15} aria-hidden="true" />
          </button>
          {/* Fuera del popover y no dentro, al lado del periodo y la oferta: los
              de ahí acotan una lista que se viene a leer, y éste es el que hay
              que poder apagar de un vistazo después de llegar aquí desde el
              aviso. Escondido detrás del embudo, quien entrara filtrado no
              tendría cómo saber por qué ve tres cobros de cuatrocientos.
              Sólo existe si hay alguna: un interruptor permanente de algo que
              casi nunca pasa es ruido en la barra. */}
          {disputedCount > 0 ? (
            <button
              type="button"
              className={`dash-orders-dispute-toggle${disputed ? ' is-on' : ''}`}
              aria-pressed={disputed}
              onClick={() => setDisputed((on) => !on)}
            >
              <IconAlertTriangle size={14} aria-hidden="true" />
              {disputedCount === 1 ? t('orders.disputeCount.one') : t('orders.disputeCount.many', { count: disputedCount })}
            </button>
          ) : null}
          {toShipCount > 0 || toShip ? (
            <button
              type="button"
              className={`dash-orders-dispute-toggle is-ship${toShip ? ' is-on' : ''}`}
              aria-pressed={toShip}
              onClick={() => setToShip((on) => !on)}
            >
              <IconTruckDelivery size={14} aria-hidden="true" />
              {t('orders.toShipCount', { count: toShipCount })}
            </button>
          ) : null}
          <div className="dash-filter-wrap" ref={filterRef}>
            <button
              className="dash-filter-trigger"
              type="button"
              aria-label={t('orders.filtersAria')}
              data-tooltip={t('orders.filtersAria')}
              aria-expanded={filterOpen}
              onClick={() => setFilterOpen((open) => !open)}
            >
              <IconAdjustmentsHorizontal size={15} aria-hidden="true" />
              {filtersOn ? <i className="dash-filter-active-dot" aria-hidden="true" /> : null}
            </button>
            {filterOpen ? (
              <div className="dash-filter-popover" role="dialog" aria-label={t('orders.filtersDialogAria')}>
                <div className="dash-filter-popover-head">
                  <strong>{t('orders.filterTitle')}</strong>
                  <button type="button" aria-label={t('orders.closeFilters')} onClick={() => setFilterOpen(false)}><IconX size={15} /></button>
                </div>

                <div className="dash-filter-section">
                  <span className="dash-filter-section-title">{t('orders.filterPeriod')}</span>
                  <div className="dash-filter-options">
                    {(['all', '7', '30', '90'] as const).map((value) => (
                      <button key={value} className={period === value ? 'active' : ''} type="button" onClick={() => setPeriod(value)}>
                        {value === 'all' ? <IconLayoutGrid size={14} /> : <IconClock size={14} />}
                        <span>{t(PERIOD_KEY[value])}</span>
                        {period === value ? <IconCheck size={14} /> : null}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sin ofertas vendidas no hay nada por lo que filtrar, y una
                    sola tampoco reparte nada: la sección aparece a partir de dos. */}
                {!scopedToOffer && soldOffers.length > 1 ? (
                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('orders.filterOffer')}</span>
                    <div className="dash-filter-options">
                      <button className={offerFilter === 'all' ? 'active' : ''} type="button" onClick={() => setOfferFilter('all')}>
                        <IconLayoutGrid size={14} /><span>{t('orders.allOffers')}</span>{offerFilter === 'all' ? <IconCheck size={14} /> : null}
                      </button>
                      {soldOffers.map((offer) => (
                        <button key={offer.id} className={offerFilter === offer.id ? 'active' : ''} type="button" onClick={() => setOfferFilter(offer.id)}>
                          <IconTag size={14} /><span>{offer.title}</span>{offerFilter === offer.id ? <IconCheck size={14} /> : null}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {filtersOn ? (
                  <button className="dash-filter-reset" type="button" onClick={resetFilters}>
                    {t('orders.resetFilters')}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </header>
      ) : null}

      {orders.length > 0 ? (
        <Pagination page={paged.page} totalPages={paged.totalPages} total={paged.total} start={paged.start} end={paged.end} onPage={paged.setPage} unit={t('orders.unit')} />
      ) : null}

      {failed ? (
        <div className="dash-inline-msg">
          <span>
            {t('orders.loadFailed')}
            {failureReason ? <code className="dash-failure-detail">{failureReason}</code> : null}
          </span>
          <div className="dash-inline-msg-actions">
            <button type="button" className="dash-secondary-btn" onClick={onReload}>
              {t('orders.retry')}
            </button>
          </div>
        </div>
      ) : sellLocked && onOpenPlans ? (
        <UpsellCard standalone copy={PAYMENTS_UPSELL} onOpenPlans={onOpenPlans} />
      ) : orders.length === 0 ? (
        <div className="dash-placeholder">
          <IconShoppingCart size={34} aria-hidden="true" />
          {/* Dentro de una oferta se habla de ELLA: «todavía no hay ventas» en
              su ficha se lee como que no las hay en todo el sitio. */}
          <strong>{empty?.title ?? t(scopedToOffer ? 'orders.empty.offerTitle' : 'orders.empty.title')}</strong>
          <span>{empty?.body ?? t(scopedToOffer ? 'orders.empty.offerBody' : 'orders.empty.body')}</span>
        </div>
      ) : visible.length === 0 ? (
        <div className="dash-placeholder">
          <IconSearch size={34} aria-hidden="true" />
          <strong>{t('orders.noResults.title')}</strong>
          <span>{t('orders.noResults.body')}</span>
          <button type="button" className="dash-secondary-btn" onClick={() => { setQuery(''); resetFilters() }}>
            {t('orders.clearFilters')}
          </button>
        </div>
      ) : (
        <>
        <div className="dash-cms-table-scroll">
          <div className={`dash-data-table dash-orders-data-table${scopedToOffer ? ' is-offer-scoped' : ''}`}>
            {/* La fila de rótulos, también aquí.
                Se quitó pensando que sólo repetía en gris lo que se lee debajo,
                y eso pasa por alto que dos de sus rótulos no son rótulos sino
                BOTONES: importe y fecha son los que ordenan la lista. Sin ella,
                la única forma de ver la compra más grande de una oferta era
                recorrerla entera. */}
            <div className="dash-data-header" role="row">
              {/* Dentro de una oferta, la primera columna ya no es qué se
                  vendió —se sabe— sino quién lo compró, y la de al lado sobra. */}
              <span>{t(scopedToOffer ? 'orders.col.buyer' : 'orders.col.offer')}</span>
              {scopedToOffer ? null : <span>{t('orders.col.buyer')}</span>}
              <button
                className={sort === 'amountHigh' || sort === 'amountLow' ? 'is-active' : ''}
                type="button"
                onClick={() => setSort((current) => (current === 'amountHigh' ? 'amountLow' : 'amountHigh'))}
              >
                {t('orders.col.amount')} <IconChevronDown className={sort === 'amountLow' ? 'is-asc' : ''} size={13} />
              </button>
              <span>{t('orders.col.status')}</span>
              <button
                className={sort === 'recent' || sort === 'oldest' ? 'is-active' : ''}
                type="button"
                onClick={() => setSort((current) => (current === 'recent' ? 'oldest' : 'recent'))}
              >
                {t('orders.col.date')} <IconChevronDown className={sort === 'oldest' ? 'is-asc' : ''} size={13} />
              </button>
              <span className="dash-data-header-actions">{t('orders.col.actions')}</span>
            </div>

            <div className="dash-project-list">
              {paged.pageItems.map((order) => (
                <article className="dash-project-row dash-data-row dash-loaded-content-in" key={order.id} onClick={openOnRowClick(() => setOpenOrder(order))}>
                  {/* La oferta es el título: es lo que se busca al recorrer la
                      lista —qué se vendió—, y el comprador es de quién.

                      Salvo dentro de la ficha de una oferta, donde la respuesta
                      a «qué se vendió» es la misma en todas las filas y la que
                      cambia es quién. Ahí manda el comprador, con el correo
                      debajo, y su columna de al lado desaparece. */}
                  <div
                    className="dash-project-row-main"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => { if (event.key === 'Enter') setOpenOrder(order) }}
                  >
                    {/* La portada de la oferta, como en Contenido. Una venta se
                        reconoce antes por la imagen de lo que se vendió que por
                        leer su título, y son varias filas de la misma oferta.
                        Sin imagen —o con la oferta borrada— queda el icono.
                        Dentro de una oferta la portada tampoco distingue nada:
                        es la misma en todas, y su sitio lo ocupa la persona. */}
                    {scopedToOffer ? (
                      /*
                        Un avatar y no el icono de cristal de las listas.
                        Aquélla es la pieza que sustituye a una PORTADA cuando no
                        la hay —mide 94×60 y lleva un degradado claro que la
                        levanta del papel—, y aquí lo que falta no es una portada
                        sino la cara de una persona. En oscuro además ese
                        degradado se convertía en una placa plateada: es el
                        elemento más brillante de la pantalla para el dato menos
                        importante de la fila.
                      */
                      <span className="dash-order-buyer-avatar dash-widget-glass-icon" aria-hidden="true">
                        <IconUser size={16} />
                      </span>
                    ) : order.offerImage ? (
                      <span className="dash-project-row-thumb dash-page-project-thumb">
                        <img src={order.offerImage} alt="" loading="lazy" />
                        {/* Varios productos: la foto del primero con cuántos más. */}
                        {!scopedToOffer && orderProductCount(order) ? (
                          <span className="dash-order-thumb-more" aria-hidden="true">+{orderProductCount(order)!.products - 1}</span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="dash-project-row-thumb dash-page-project-thumb dash-row-glass-fallback">
                        <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog" aria-hidden="true">
                          <IconTag size={13} />
                        </span>
                      </span>
                    )}
                    <span className="dash-project-row-copy">
                      <span className="dash-project-row-title">
                        {isNewOrder(order) ? <NewItemDot /> : null}
                        {/* El número que cita el cliente: lo primero que se busca. */}
                        {order.number ? <span className="dash-order-number">#{order.number}</span> : null}
                        <strong>
                          {scopedToOffer
                            ? (order.buyerName || order.buyerEmail || t('orders.noName'))
                            /* Varios productos: cuántos, no el primero con «(+4)». */
                            : orderProductCount(order)
                              ? orderCountTitle(orderProductCount(order)!, t)
                              : (order.offerTitle || t('orders.deletedOffer'))}
                        </strong>
                      </span>
                      {/* El correo bajo el nombre: con dos «Juan Pérez» es lo
                          único que los distingue. Sólo aquí, porque en Cobros
                          esa segunda línea vive en su propia columna. */}
                      {scopedToOffer && order.buyerName && order.buyerEmail ? (
                        <span className="dash-project-row-details">{order.buyerEmail}</span>
                      ) : null}
                      {/* Qué talla, qué color y cuántas: es lo que hay que
                          meter en la caja, y sin abrir la ficha. */}
                      {orderProductCount(order) && !scopedToOffer ? (
                        <span className="dash-project-row-details">{orderItemsCompact(order)}</span>
                      ) : orderItemSummary(order) ? (
                        <span className="dash-project-row-details">{orderItemSummary(order)}</span>
                      ) : null}
                    </span>
                  </div>

                  <div className="dash-list-row-meta dash-order-row-meta">
                    {/* Nombre y correo son la misma respuesta —quién compró—, así
                        que van en una columna y no en dos: la de al lado, la
                        oferta, es la que necesita el ancho que sobraba. Pulsando
                        se abre su ficha en Contactos, que es donde está todo lo
                        demás que sabemos de esa persona; escribirle sigue estando
                        en la ficha del cobro. */}
                    {scopedToOffer ? null : (
                    <span className="dash-data-cell dash-order-buyer-cell" data-label={t('orders.col.buyer')}>
                      {onOpenContact && contactIdentity(order.buyerEmail) ? (
                        <button
                          type="button"
                          className="dash-order-buyer is-link"
                          title={t('orders.viewBuyerAria', { email: order.buyerEmail ?? '' })}
                          onClick={() => onOpenContact(contactIdentity(order.buyerEmail))}
                        >
                          <strong>{order.buyerName || t('orders.noName')}</strong>
                          <span>{order.buyerEmail}</span>
                        </button>
                      ) : (
                        <span className="dash-order-buyer">
                          <strong>{order.buyerName || <em>{t('orders.noName')}</em>}</strong>
                          <span><em>{t('orders.noEmail')}</em></span>
                        </span>
                      )}
                    </span>
                    )}
                    {/*
                      * Con disputa, aquí va la disputa y no el importe.
                      *
                      * En la banda estrecha —la ficha de una oferta, el
                      * teléfono— la columna de estado se oculta y el importe se
                      * convierte en una cápsula verde, porque es lo único por
                      * lo que se recorre la lista. El efecto era que un cobro
                      * que el banco está reclamando se leía como «59,00 € en
                      * verde»: la fila afirmaba en el color del dinero cobrado
                      * justo lo que está en duda.
                      *
                      * Así que cuando hay disputa, esta celda deja de hablar de
                      * dinero. La cifra no se pierde: sigue en el `title` y
                      * entera en la ficha del cobro.
                      */}
                    <span
                      className={`dash-data-cell dash-order-amount-cell${orderDisputed(order.disputeStatus) ? ' is-disputed' : ''}`}
                      data-label={t('orders.col.amount')}
                      {...(orderDisputed(order.disputeStatus)
                        ? { title: t('orders.disputedTitle', { amount: formatTotal(order.disputeAmount, order.currency) }) }
                        : {})}
                    >
                      <strong className="dash-order-amount-money">{order.granted ? t('orders.granted') : order.formatted}</strong>
                      {/* Las DOS versiones en el árbol, y que elija el CSS.
                          Quién gana depende del ancho del contenedor —si la
                          columna de estado está o no— y eso React no lo sabe:
                          es una container query. Con un `if` aquí, la banda
                          ancha decía «Disputa» dos veces, en el importe y en el
                          estado. */}
                      {orderDisputed(order.disputeStatus) ? (
                        <strong className="dash-order-amount-dispute">
                          <IconAlertTriangle size={12} aria-hidden="true" />
                          {t(disputeNeedsAction(order.disputeStatus) ? 'orders.disputed' : 'orders.underReview')}
                        </strong>
                      ) : null}
                    </span>
                    <span
                      className={`dash-data-cell dash-order-status-cell${order.fulfillmentStatus && order.status !== 'refunded' && !orderDisputed(order.disputeStatus) ? ' is-fulfillment' : ''}`}
                      data-label={t('orders.col.status')}
                    >
                      {/* La disputa desplaza al estado en vez de ponerse al
                          lado. Son dos ejes —uno dice cuánto del dinero se
                          quedó, el otro que alguien lo está reclamando— pero en
                          una celda de lista sólo cabe la respuesta a «¿tengo que
                          hacer algo con esto?», y mientras hay disputa esa
                          respuesta es la disputa. El resto está en la ficha. */}
                      {disputeNeedsAction(order.disputeStatus) ? (
                        <span className="dash-offer-status is-danger" title={t('orders.disputedTitle', { amount: formatTotal(order.disputeAmount, order.currency) })}>
                          <IconAlertTriangle size={12} aria-hidden="true" />
                          {t('orders.disputed')}
                        </span>
                      ) : disputeUnderReview(order.disputeStatus) ? (
                        /* Respondida y sin veredicto. Va en gris y no en rojo
                           porque el rojo de esta tabla significa «hazlo antes de
                           una fecha» y aquí no hay nada que hacer; pero sigue
                           desplazando al estado, porque decir «Pagado» de un
                           cobro que el banco está decidiendo es prometer un
                           dinero que puede irse. */
                        <span className="dash-offer-status is-review" title={t('orders.underReviewTitle', { amount: formatTotal(order.disputeAmount, order.currency) })}>
                          <IconClock size={12} aria-hidden="true" />
                          {t('orders.underReview')}
                        </span>
                      ) : order.fulfillmentStatus && order.status !== 'refunded' ? (
                        /* Un producto físico: lo que importa de un vistazo es si
                           ya salió. Lo cobrado se sigue leyendo en el importe. */
                        <span className={`dash-offer-status ${fulfillmentTone(order.fulfillmentStatus)}`}>
                          {order.fulfillmentStatus === 'unfulfilled' ? <IconTruckDelivery size={12} aria-hidden="true" /> : null}
                          {t(FULFILLMENT_KEY[order.fulfillmentStatus])}
                        </span>
                      ) : (
                        <span className={`dash-offer-status ${orderStatusTone(order.status)}`}>
                          {orderStatusLabel(order.status, t)}
                        </span>
                      )}
                    </span>
                    <time className="dash-data-cell dash-list-date-cell" data-label={t('orders.col.date')} dateTime={order.createdAt}>
                      <IconCalendarEvent className="dash-list-meta-icon" size={14} aria-hidden="true" />
                      {formatDashboardListDate(order.createdAt)}
                    </time>
                  </div>

                  <div className="dash-project-row-actions">
                    <button type="button" className="dash-secondary-btn dash-project-row-action dash-order-open-btn" onClick={() => setOpenOrder(order)}>
                      <IconEye size={14} aria-hidden="true" />
                      <span className="dash-project-row-action-label">{t('orders.open')}</span>
                    </button>
                    {/* Sólo se ve en el teléfono. Ahí «Abrir» desaparece —toda la
                        ficha abre la venta— y el comprador tampoco cabe, así que
                        éste es el único sitio desde donde se le alcanza. */}
                    <div className={`dash-project-row-menu-wrap${menuOpenId === order.id ? ' is-open' : ''}${menuOpenId === order.id && listMenu.up ? ' menu-up' : ''}`}>
                      <button
                        className="project-menu-btn dash-icon-btn dash-page-options-btn"
                        type="button"
                        aria-label={t('orders.moreOptions')}
                        data-tooltip={t('orders.options')}
                        aria-expanded={menuOpenId === order.id}
                        onClick={(event) => {
                          event.stopPropagation()
                          /* Se mide al abrir, con las mismas cuentas que Ofertas:
                             en las últimas filas no cabe hacia abajo y hay que
                             darle la vuelta, y en cualquier caso el menú no puede
                             salirse de la ventana por ningún lado. */
                          if (menuOpenId !== order.id) {
                            const rect = event.currentTarget.getBoundingClientRect()
                            const menuWidth = 184
                            const menuHeight = 148
                            const edge = 12
                            const gap = 8
                            const spaceBelow = window.innerHeight - rect.bottom - edge
                            const spaceAbove = rect.top - edge
                            const up = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow
                            const top = up
                              ? Math.max(edge, rect.top - menuHeight - gap)
                              : Math.min(window.innerHeight - menuHeight - edge, rect.bottom + gap)
                            const left = Math.max(edge, Math.min(window.innerWidth - menuWidth - edge, rect.right - menuWidth))
                            setListMenu({ top, left, up, maxHeight: Math.max(menuHeight, up ? spaceAbove - gap : spaceBelow - gap) })
                          }
                          setMenuOpenId(menuOpenId === order.id ? null : order.id)
                        }}
                      >
                        <IconDots size={16} />
                      </button>
                      {menuOpenId === order.id ? createPortal(
                        <div
                          className={`project-menu dash-project-row-menu dash-order-row-menu-portal${listMenu.up ? ' is-up' : ''}`}
                          role="menu"
                          style={{ top: listMenu.top, left: listMenu.left, maxHeight: listMenu.maxHeight, overflowY: 'auto' }}
                        >
                          <button
                            className="project-menu-item"
                            type="button"
                            role="menuitem"
                            onClick={() => { setMenuOpenId(null); setOpenOrder(order) }}
                          >
                            <IconEye size={14} /> {t('orders.menu.view')}
                          </button>
                          {order.buyerEmail ? (
                            <>
                              <div className="project-menu-rule" role="separator" />
                              {onOpenContact && contactIdentity(order.buyerEmail) ? (
                                <button
                                  className="project-menu-item"
                                  type="button"
                                  role="menuitem"
                                  onClick={() => { setMenuOpenId(null); onOpenContact(contactIdentity(order.buyerEmail)) }}
                                >
                                  <IconUser size={14} /> {t('orders.menu.buyer')}
                                </button>
                              ) : null}
                              <a className="project-menu-item" role="menuitem" href={`mailto:${order.buyerEmail}`} onClick={() => setMenuOpenId(null)}>
                                <IconMail size={14} /> {t('orders.menu.email')}
                              </a>
                            </>
                          ) : null}
                        </div>,
                        document.body,
                      ) : null}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
        {/* Y al pie: quien acaba de recorrer la página busca el siguiente paso
            donde terminó de leer. Con una sola, sólo queda la cuenta. */}
        <Pagination page={paged.page} totalPages={paged.totalPages} total={paged.total} start={paged.start} end={paged.end} onPage={paged.setPage} unit={t('orders.unit')} alwaysShow />
        </>
      )}

      {openOrder ? (
        <OrderDetailDialog
          projectId={projectId}
          token={token}
          order={openOrder}
          onOpenOffer={onOpenOffer}
          {...(onOpenContact ? { onOpenContact } : {})}
          /* Cerrar la ficha vuelve a pedir la lista: dentro se pudo reembolsar,
             y el webhook de Stripe llega en paralelo. Es una petición por cierre,
             no un sondeo. */
          onClose={() => { setOpenOrder(null); onRecordChange?.(null); onReload() }}
        />
      ) : null}
    </>
  )
}
